import { css } from '@emotion/react'
import { fetchAboutUsPageContent } from 'api/contentful/aboutUsPage'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import { AboutHero } from 'components/content/about/aboutHero/AboutHero'
import { AboutTeam } from 'components/content/about/aboutTeam/AboutTeam'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { IAboutUsPageFields } from 'types/generated/contentful'
import { REVALIDATE_SECS } from 'utils/constants'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { BREAKPOINTS } from 'utils/styled'

interface AboutUsPageStaticProps {
  content: IAboutUsPageFields
  faq: ContentfulFAQ
}

export default function Page(props: AboutUsPageStaticProps) {
  const { t } = useTranslation('security')
  return (
    <Layout
      header={<TopNavigationLandingPage variant="general-info" />}
      footer={<Footer variant="estate-planning" />}
    >
      <PageTitle title={t('security.about.meta_title')} />
      <PageMeta description={t('security.about.meta_desc')} image="/images/preview/about.png" />
      <AboutHero
        content={{
          aboutHeroCaption: t('security.about.hero_caption'),
          aboutHeroTitle: t('security.about.hero_title'),
          aboutHeroDescription: t('security.about.hero_desc'),
        }}
      />

      <AboutTeam content={{ ...props.content }} />
      <FaqTwoColumns
        content={props.faq}
        css={css`
          padding: 2.25rem 0 8.75rem;
          @media ${BREAKPOINTS.MD.max} {
            padding: 2rem 0 3rem;
          }
        `}
      />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const content = await fetchAboutUsPageContent()
  if (!content) throw Error('Could not fetch About Us page content.')
  const faq = await getContentfulFAQ('about-us')

  const props: AboutUsPageStaticProps = {
    ...(await getIntlProps(ctx, 'security')),
    ...(await getContentfulImages()),
    content,
    faq,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
